import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useCms } from "@/lib/cms";
import { BACKEND_BASE } from "@/lib/backend";
import CountUp from "@/components/CountUp";
import ResponsivePreviewSection from "@/components/ResponsivePreviewSection";
import { handleContactClick } from "@/lib/contactForm";
import {
  HeroBlock,
  HERO_STATS,
  HERO_SPLINE_EMBED_KEY,
  HERO_SPLINE_URL,
  HERO_SPLINE_RENDER_WIDTH_MOBILE,
  HERO_SPLINE_RENDER_HEIGHT_MOBILE,
  HERO_SPLINE_MOBILE_SCALE_INSET,
  HERO_SPLINE_MOBILE_STAGE_OFFSET_Y,
  getHeroStatCountUpProps,
  formatPreviewStat,
  parseTitleLines,
  parseHeroSubtitle,
  HERO_LEFT_COPY_ENABLED,
  HERO_MOBILE_CONTENT_ENABLED,
  HERO_AI_BAR_ENABLED,
  MOBILE_HERO_USE_VESPER_SPLINE,
  MOBILE_HERO_FALLBACK_IMG,
} from "@/components/sections/hero/heroShared";
import HeroAmbientBg from "@/components/sections/hero/HeroAmbientBg";
import HeroAiConsultBar from "@/components/sections/hero/HeroAiConsultBar";
import HeroSplineLoadingOverlay from "@/components/sections/hero/HeroSplineLoadingOverlay";
import SplineScaledIframe from "@/components/sections/hero/SplineScaledIframe";
import { prefersReducedHeroMotion } from "@/components/sections/hero/useHeroSplineVisibility";
import "./HeroSection.css";
import "./HeroSectionMobile.css";

const absUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND_BASE}${u}`) : "");

/** 모바일 — 애니메이션 대기 없이 문구 즉시 표시 */
const MOBILE_HERO_MOTION = {
  initial: { opacity: 1, y: 0 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0 },
};

/** Mobile-only hero (≤600px). Desktop uses HeroSectionDesktop. */
export default function HeroSectionMobile({
  showDevicePreview = true,
  embedMode = false,
  previewDevice,
}) {
  const { cms } = useCms();
  const isInlinePreview = Boolean(previewDevice);
  const highlight = (cms.hero_title_highlight || "성장").trim();
  const { titleLine1, titleLine2Before, titleLine2Highlight, titleLine2After } = parseTitleLines(
    cms.hero_title,
    highlight,
  );

  const heroVideo = absUrl(cms.media_hero_bg_video);
  const heroImage = absUrl(cms.media_hero_bg_image);
  const useMediaBg = Boolean(heroVideo || heroImage);
  const useVesperSpline = MOBILE_HERO_USE_VESPER_SPLINE && !useMediaBg && !prefersReducedHeroMotion();
  const [splineReady, setSplineReady] = useState(false);

  useEffect(() => {
    if (!useVesperSpline) {
      setSplineReady(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setSplineReady(true), 12000);
    return () => window.clearTimeout(timer);
  }, [useVesperSpline]);

  const titleLine2 =
    titleLine2Before || titleLine2Highlight || titleLine2After
      ? `${titleLine2Before}${titleLine2Highlight}${titleLine2After}`
      : "";
  const { lead: subtitleLead, body: subtitleBody } = parseHeroSubtitle(cms.hero_subtitle);
  const showMobileContent = HERO_LEFT_COPY_ENABLED || HERO_MOBILE_CONTENT_ENABLED;

  return (
    <section
      data-testid="hero-section-mobile"
      className={`ds-hero-section ds-hero-section--mobile${embedMode ? " ds-hero-section--embed" : ""}`}
      id="hero"
    >
      {!useMediaBg ? <HeroAmbientBg /> : null}

      {!useMediaBg ? (
        <div
          className="ds-hero-spline-wrapper is-mounted"
          aria-hidden="true"
          data-testid={useVesperSpline ? "hero-mobile-robot-spline" : "hero-mobile-static-visual"}
        >
          {useVesperSpline ? (
            <>
              <HeroSplineLoadingOverlay visible={!splineReady} />
              <SplineScaledIframe
                key={HERO_SPLINE_EMBED_KEY}
                src={HERO_SPLINE_URL}
                title="DSite hero background"
                className="ds-hero-spline-mobile-scaled"
                iframeClassName={`ds-hero-spline-bg-embed${splineReady ? " is-ready" : ""}`}
                scaleMode="contain"
                renderWidth={HERO_SPLINE_RENDER_WIDTH_MOBILE}
                renderHeight={HERO_SPLINE_RENDER_HEIGHT_MOBILE}
                scaleInset={HERO_SPLINE_MOBILE_SCALE_INSET}
                stageOffsetY={HERO_SPLINE_MOBILE_STAGE_OFFSET_Y}
                loading="eager"
                onLoad={() => setSplineReady(true)}
              />
            </>
          ) : (
            /* prefers-reduced-motion: ambient glow only (optional PNG: hero-mobile-vesper.png) */
            <img
              src={MOBILE_HERO_FALLBACK_IMG}
              alt=""
              className="ds-hero-mobile-static-image is-ready"
              decoding="async"
              draggable={false}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
        </div>
      ) : null}

      {useMediaBg ? (
        <div className="ds-hero-spline-bg-wrapper" aria-hidden="true">
          {heroVideo ? (
            <video
              data-testid="hero-bg-video"
              src={heroVideo}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="ds-hero-bg-media"
            />
          ) : (
            <div
              data-testid="hero-bg-image"
              className="ds-hero-bg-media ds-hero-bg-media--image"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
          )}
        </div>
      ) : null}

      {showMobileContent ? (
      <div className="ds-hero-mobile-content">
        <HeroBlock
          embedMode={embedMode}
          motionConfig={MOBILE_HERO_MOTION}
          data-testid="hero-badge"
          className="ds-hero-badge ds-hero-glass ds-hero-clickable"
        >
          {absUrl(cms.media_hero_badge_icon) ? (
            <img
              src={absUrl(cms.media_hero_badge_icon)}
              alt=""
              data-testid="hero-badge-icon"
              className="h-4 w-4 object-contain"
            />
          ) : (
            <span className="ds-hero-badge-mark" aria-hidden="true">
              D
            </span>
          )}
          <span>{cms.hero_badge_text || "DSITESOLUTION"}</span>
          <span className="pulse-dot" />
        </HeroBlock>

        <HeroBlock
          embedMode={embedMode}
          as="h1"
          motionConfig={MOBILE_HERO_MOTION}
          data-testid="hero-title"
          className="ds-hero-title ds-hero-clickable ds-cms-text"
        >
          <span className="ds-hero-title-line">{titleLine1}</span>
          {titleLine2 ? (
            <span className="ds-hero-title-line ds-hero-title-line--sub">
              {titleLine2Before}
              {titleLine2Highlight ? (
                <span data-testid="hero-title-highlight" className="ds-hero-title-accent">
                  {titleLine2Highlight}
                </span>
              ) : null}
              {titleLine2After}
            </span>
          ) : null}
        </HeroBlock>

        {showDevicePreview && !embedMode ? (
          <HeroBlock
            embedMode={embedMode}
            motionConfig={MOBILE_HERO_MOTION}
            className="ds-hero-preview-wrap ds-hero-clickable"
          >
            <ResponsivePreviewSection variant="hero-inline" />
          </HeroBlock>
        ) : null}

        <HeroBlock
          embedMode={embedMode}
          motionConfig={MOBILE_HERO_MOTION}
          data-testid="hero-subtitle"
          className="ds-hero-subtitle ds-hero-clickable ds-cms-text"
        >
          {subtitleLead ? <span className="ds-hero-subtitle-lead">{subtitleLead}</span> : null}
          {subtitleBody ? (
            <span className="ds-hero-subtitle-body whitespace-pre-line">{subtitleBody}</span>
          ) : null}
        </HeroBlock>

        <HeroBlock
          embedMode={embedMode}
          motionConfig={MOBILE_HERO_MOTION}
          className="ds-hero-cta-row ds-hero-clickable"
        >
          <a
            href={cms.cta_primary_link || "#contact"}
            data-testid="hero-primary-cta"
            className="ds-hero-btn-primary"
            onClick={(e) => handleContactClick(e, cms.cta_primary_link || "#contact")}
          >
            {cms.cta_primary_text || "무료 상담 신청"}
            <ArrowRight size={16} />
          </a>
          <a
            href={cms.cta_secondary_link || "#services"}
            data-testid="hero-secondary-cta"
            className="ds-hero-btn-secondary ds-hero-glass"
          >
            {cms.cta_secondary_text || "서비스 살펴보기"}
          </a>
        </HeroBlock>

        <HeroBlock
          embedMode={embedMode}
          motionConfig={MOBILE_HERO_MOTION}
          className="ds-hero-stats ds-hero-clickable"
        >
          {HERO_STATS.map((stat, index) => (
            <div key={stat.testId} className="ds-hero-stat-item" data-stat-id={stat.testId}>
              <div className={`ds-hero-stat-value${stat.accent ? " is-accent" : ""}`}>
                {isInlinePreview || embedMode ? (
                  <span data-testid={stat.testId}>{formatPreviewStat(stat)}</span>
                ) : (
                  <CountUp {...getHeroStatCountUpProps(stat, index)} />
                )}
              </div>
              <div className="ds-hero-stat-label">{stat.label}</div>
            </div>
          ))}
        </HeroBlock>
      </div>
      ) : null}

      {HERO_AI_BAR_ENABLED && !embedMode && !showMobileContent ? (
        <HeroAiConsultBar pinned />
      ) : null}
    </section>
  );
}
